import { PROFILE, USERS, CATEGORIES } from "@/lib/constants/api";
import API from "@/lib/constants/axios";
import { CategoryType, UserProfileType, UserType } from "@/types/user.types";
import { toast } from "sonner";
import { create } from "zustand";

interface UserState {
  user: UserType | undefined;
  userId: string | undefined;
  userProfile: UserProfileType | undefined;
  categories: CategoryType[];
  getUserProfile: () => void;
  getUser: (userId: string) => void;
  setUserId: (userId: string | undefined) => void;
  uploadProfilePicture: (
    data: any,
  ) => Promise<{ success: boolean; message: string }>;
  uploadBackgroundPicture: (data: any) => void;
  updateSocialMediaLinks: (data: any) => void;
  updateUserProfile: (
    data: any,
  ) => Promise<{ success: boolean; message: string }>;
  updateUserName: (data: any) => Promise<{ success: boolean; message: string }>;
  getCategories: () => void;
  updateUserDetails: (id: string, payload: Partial<UserType>) => void;
}

const useUserStore = create<UserState>()((set, get) => ({
  user: undefined,
  userProfile: undefined,
  userId: undefined,
  categories: [],
  setUserId: (userId: string | undefined) => ({ userId }),
  getUser: async (userId: string) => {
    try {
      const res = await API.get(`${USERS}/${userId}`);
      set({ user: res.data });
    } catch (error) {}
  },
  getUserProfile: async () => {
    try {
      const res = await API.get(PROFILE);
      set({ userProfile: res.data });
    } catch (error) {}
  },
  uploadProfilePicture: async (data: any) => {
    try {
      const res = await API.post(`${PROFILE}/image`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status == 201) {
        const oldUserProfile = get().userProfile || ({} as UserProfileType);
        if (oldUserProfile) {
          oldUserProfile["profile_url"] = res.data["imageUrl"][0];
          set({ userProfile: oldUserProfile });
        }
        return { success: true, message: "Profile updated successfully!" };
      }
      return { success: false, message: "Failed to update profile!" };
    } catch (error) {
      return { success: false, message: "Failed to update profile!" };
    }
  },
  uploadBackgroundPicture: async (data: any) => {
    try {
      const res = await API.post(`${PROFILE}/background-image`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status == 201) {
        const oldUserProfile = get().userProfile || ({} as UserProfileType);
        if (oldUserProfile) {
          oldUserProfile["background_url"] = res.data["imageUrl"][0];

          set({ userProfile: oldUserProfile });
        }
      }
    } catch (error) {}
  },
  updateSocialMediaLinks: async (data) => {
    try {
      const res = await API.patch(`${PROFILE}`, data);

      if (res.status === 200) {
        toast.success("Social media links updated successfully!");
      }
    } catch (error) {
      toast.error("Social media links failed to update!");
    }
  },
  updateUserProfile: async (data) => {
    try {
      const res = await API.patch(`${PROFILE}`, data);

      if (res.status == 200) {
        get().getUserProfile();
        return { success: true, message: "Profile updated successfully!" };
      }
      return { success: false, message: "Failed to update profile!" };
    } catch (error) {
      return { success: false, message: "Failed to update profile!" };
    }
  },
  updateUserName: async (data) => {
    try {
      const res = await API.patch(`${USERS}/user-name`, data);
      if (res.status == 200) {
        return { success: true, message: "Profile updated successfully!" };
      }
      return { success: false, message: "Failed to update profile!" };
    } catch (error) {
      return { success: false, message: "Failed to update profile!" };
    }
  },
  getCategories: async () => {
    try {
      const categories = await API.get(CATEGORIES);
      set({ categories: categories.data });
    } catch (error) {}
  },
  updateUserDetails: async (id: string, payload: Partial<UserType>) => {
    try {
      const res = await API.patch(`${USERS}/${id}`, payload);
      if (res.status == 200) {
        get()?.getUser(id);
        toast.success(
          res?.data?.message ?? "User details updated successfully",
        );
      } else {
        toast.error(res?.data?.message ?? "User details updated successfully");
      }
    } catch (error) {
      toast.error("Error while updating user details");
    }
  },
}));

export default useUserStore;
